import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { RelatedEntityModel } from '@models/relatedEntity'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/relatedEntity/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/relatedEntity/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/relatedEntity/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/relatedEntity/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/relatedEntity/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/relatedEntity/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/relatedEntity/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/relatedEntity/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/relatedEntity/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/relatedEntity/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/relatedEntity/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/relatedEntity/DELETE_FAILURE'

export type RelatedEntityModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: RelatedEntityModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadRelatedEntities(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<RelatedEntityModel>> => {
    const loadedForKeys = [...getState().relatedEntities.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/related_entities.json', options)
      .then(({ data }: { data: RelatedEntityModel[] }) => {
        const normalizedJson = normalize(data, Schemas.RELATED_ENTITY_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    // @ts-expect-error TS2322
    return promise
  }
}

export function loadRelatedEntity(relatedEntityId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.relatedEntities.loadedIds]

    if (relatedEntityId && !loadedIds.includes(relatedEntityId)){
      loadedIds.push(relatedEntityId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/related_entities/${relatedEntityId}.json`, options)
      .then(({ data }: { data: RelatedEntityModel }) => {
        const normalizedJson = normalize(data, Schemas.RELATED_ENTITY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createRelatedEntity(relatedEntity: Partial<RelatedEntityModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      related_entity: relatedEntity,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/related_entities.json', options, config)
      .then(({ data }: { data: RelatedEntityModel }) => {
        const normalizedJson = normalize(data, Schemas.RELATED_ENTITY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateRelatedEntity(
  // @ts-expect-error TS2344
  relatedEntity: UpdateParams<RelatedEntityModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      related_entity: relatedEntity,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/related_entities/${relatedEntity.id}.json`, options, config)
      .then(({ data }: { data: RelatedEntityModel }) => {
        const normalizedJson = normalize(data, Schemas.RELATED_ENTITY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// @ts-expect-error TS2344
export function deleteRelatedEntity(relatedEntity: DeleteParams<RelatedEntityModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/related_entities/${relatedEntity.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.RELATED_ENTITY)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: RelatedEntityModuleState = initialState,
  action: Action = { type: '' },
): RelatedEntityModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
